import { jsx } from 'react/jsx-runtime';
import { forwardRef, useRef, useImperativeHandle, useMemo, Children } from 'react';
import { useTranslation } from 'react-i18next';
import { dsI18n, getCommonClassNameDefault } from '@skatteetaten/ds-core-utils';
import { ChipsContext } from '../ChipsContext/index.esm.js';
import { ChipsRemovable } from '../ChipsRemovable/index.esm.js';
import { ChipsToggle } from '../ChipsToggle/index.esm.js';
import '@skatteetaten/ds-icons';
import '../defaults.esm.js';
import './styles.css';


var styles = {
    "chipsList": "Chips-module_chipsList__sKEnB",
    "srOnly": "Chips-module_srOnly__U-97u"
};

const Chips = /*#__PURE__*/ forwardRef(({ id, className = getCommonClassNameDefault(), lang, 'data-testid': dataTestId, ariaLabel, children }, ref)=>{
    const { t } = useTranslation('ds_collections', {
        i18n: dsI18n
    });
    const listRef = useRef(null);
    const noFiltersRef = useRef(null);
    useImperativeHandle(ref, ()=>listRef.current);
    const updateFocus = useMemo(()=>(removedChip)=>{
            if (!listRef.current || !removedChip.parentElement) return;
            const listItemsArray = Array.from(listRef.current.children);
            if (listItemsArray.length === 1) {
                // fjerner siste chip, sett fokus til skjult tekst
                setTimeout(()=>noFiltersRef.current?.focus(), 0);
                return;
            }
            const indexOfRemovedChip = listItemsArray.indexOf(removedChip.parentElement);
            const indexToFocus = indexOfRemovedChip === listItemsArray.length - 1 ? listItemsArray.length - 2 : indexOfRemovedChip;
            setTimeout(()=>{
                listItemsArray[indexToFocus]?.querySelector('button')?.focus();
            }, 0);
        }, []);
    const concatenatedClassname = `${styles.chipsList} ${className}`.trim();
    const childrenAsArray = Children.toArray(children);
    if (childrenAsArray.length === 0) {
        return /*#__PURE__*/ jsx("span", {
            ref: noFiltersRef,
            className: styles.srOnly,
            tabIndex: -1,
            children: t('chips.NoFilters')
        });
    }
    return /*#__PURE__*/ jsx(ChipsContext.Provider, {
        value: {
            updateFocus
        },
        children: /*#__PURE__*/ jsx("ul", {
            ref: listRef,
            id: id,
            className: concatenatedClassname,
            lang: lang,
            "data-testid": dataTestId,
            "aria-label": ariaLabel,
            children: childrenAsArray.map((child, index)=>{
                return /*#__PURE__*/ jsx("li", {
                    children: child
                }, index);
            })
        })
    });
});
Chips.displayName = 'Chips';
Chips.Removable = ChipsRemovable;
Chips.Removable.displayName = 'Chips.Removable';
Chips.Toggle = ChipsToggle;
Chips.Toggle.displayName = 'Chips.Toggle';

export { Chips };
